import { createContext, useContext } from "react";
import { createAuthClient } from "better-auth/react";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
// Server uses basePath: "/auth", so client baseURL must include it
export const authClient = createAuthClient({
  baseURL: `${API_URL.replace(/\/$/, "")}/auth`,
});

const { useSession } = authClient;

export type Session = Awaited<ReturnType<typeof authClient.getSession>>["data"];

/** Session user shape (Better Auth returns name + image from OAuth e.g. Google). */
export type SessionUser = {
  id?: string;
  email?: string | null;
  name?: string | null;
  image?: string | null;
  subscriptionPlan?: string;
};

type AuthSessionContextValue = {
  session: Session | null;
  isPending: boolean;
  refetch: () => Promise<unknown>;
};

const AuthSessionContext = createContext<AuthSessionContextValue | null>(null);

/** Single subscription to session; wrap app with AuthSessionProvider to avoid many get-session calls. */
export function AuthSessionProvider({ children }: { children: React.ReactNode }) {
  const value = useSession();
  return (
    <AuthSessionContext.Provider
      value={{
        session: value.data ?? null,
        isPending: value.isPending,
        refetch: value.refetch,
      }}
    >
      {children}
    </AuthSessionContext.Provider>
  );
}

export function useAuthSession(): AuthSessionContextValue {
  const ctx = useContext(AuthSessionContext);
  if (!ctx) throw new Error("useAuthSession must be used within AuthSessionProvider");
  return ctx;
}

export { useSession };
